import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useModalState } from '../../hooks/useModalState';
import { Modal, ModalProps } from '../Modal';
import { TouchButton } from '../TouchButton';
import {
  ProgramWizardData,
  ProgramWizardOperatingMode,
  ProgramWizardProps,
  ProgramWizardStep,
  ProgramWizardStepValue,
} from './ProgramWizard.types';
import {
  applyInputSettingsData,
  getDialogTitle,
  getInitialWizardData,
  getShutDown24vEnabled,
  getStepBeforeConfirm,
  getTriggerButtonText,
  isBatteryInputStillConnected,
  isTransitionComplete,
  isTransitionPhase1Complete,
  isTransitionWaitComplete,
  submitBatteryProgram,
} from './ProgramWizard.utils';
import StepConfirm from './StepConfirm';
import StepInputSettings from './StepInputSettings';
import StepPeakshaveMode, { PeakshaveMode } from './StepPeakshaveMode';
import StepProgramStarting from './StepProgramStarting';
import StepSelectProgram, { ProgramOption } from './StepSelectProgram';

const TRANSITIONING_OPERATING_MODES = [ProgramWizardOperatingMode.STANDBY, ProgramWizardOperatingMode.SHUTDOWN];

type ActiveTransition = { operatingMode: ProgramWizardOperatingMode; startedAt: number };

export * from './ProgramWizard.types';

export const ProgramWizard = ({
  cancelButtonAlign,
  batteryId,
  currentProgram,
  stateData,
  label,
  buttonSize,
  paperStyle = { minWidth: 840, top: 0, position: 'absolute' },
  isMobile = false,
  NumberFieldComponent,
  getBatteryConfig,
  onSubmit,
  onSubmitSuccess,
  onSubmitError,
  batteryInput,
  getBatteryInput,
}: ProgramWizardProps) => {
  const { t } = useTranslation('programWizard');
  const [step, setStep] = useState<ProgramWizardStepValue>(ProgramWizardStep.SELECT_PROGRAM);
  const [wizardData, setWizardData] = useState<ProgramWizardData>(getInitialWizardData(batteryId));
  const { isModalOpen, openModal, closeModal } = useModalState();
  const [selectedProgram, setSelectedProgram] = useState<ProgramOption | null>(null);
  const [selectedPeakshaveMode, setSelectedPeakshaveMode] = useState<PeakshaveMode | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showShutdownInputAlert, setShowShutdownInputAlert] = useState(false);
  const { control, handleSubmit, reset } = useForm({ mode: 'onChange' });

  // Tracks an in-flight standby/shutdown transition so the trigger button stays disabled even
  // after the modal is closed, and so the starting step's spinners can reflect the wait.
  const [activeTransition, setActiveTransition] = useState<ActiveTransition | null>(null);
  // Keep the completed transition timestamps until the modal closes so the STARTING step can
  // render the final completed state instead of regressing while the user is still reading it.
  const [phase1CompletedAt, setPhase1CompletedAt] = useState<number | null>(null);
  const [shutdownCompletedAt, setShutdownCompletedAt] = useState<number | null>(null);
  const [transitionCompleted, setTransitionCompleted] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!activeTransition) {
      return undefined;
    }
    // Refresh immediately: `now` may be stale from mount or a previous transition.
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [activeTransition]);

  useEffect(() => {
    if (!activeTransition) {
      return undefined;
    }
    if (
      phase1CompletedAt === null &&
      isTransitionPhase1Complete({ startedAt: activeTransition.startedAt, modeIsStandby: stateData.modeIsStandby, now })
    ) {
      // Stamp with real time, not the `now` state: this effect can run before the refreshed
      // `now` has committed, and a stale stamp would skip the extra wait entirely.
      setPhase1CompletedAt(Date.now());
    }

    if (
      activeTransition.operatingMode === ProgramWizardOperatingMode.SHUTDOWN &&
      stateData.systemOff &&
      shutdownCompletedAt === null
    ) {
      setShutdownCompletedAt(Date.now());
    }
  }, [activeTransition, stateData.modeIsStandby, stateData.systemOff, now, phase1CompletedAt, shutdownCompletedAt]);

  const transitionWaitComplete = activeTransition ? isTransitionWaitComplete({ phase1CompletedAt, now }) : false;

  useEffect(() => {
    if (
      activeTransition &&
      isTransitionComplete({
        operatingMode: activeTransition.operatingMode,
        stateData,
        waitComplete: transitionWaitComplete,
        shutdownCompletedAt,
        now,
      })
    ) {
      setTransitionCompleted(true);
      setActiveTransition(null);
    }
  }, [activeTransition, stateData, transitionWaitComplete, shutdownCompletedAt, now]);

  const handleClose = useCallback(() => {
    setTimeout(() => setStep(ProgramWizardStep.SELECT_PROGRAM), 500);
    closeModal();
    setWizardData(getInitialWizardData(batteryId));
    setSelectedProgram(null);
    setShowShutdownInputAlert(false);
    setSelectedPeakshaveMode(null);
    setTransitionCompleted(false);
    setIsSubmitting(false);
    if (!activeTransition) {
      setPhase1CompletedAt(null);
      setShutdownCompletedAt(null);
    }
  }, [activeTransition, batteryId, closeModal]);

  const stepConfigs: Record<ProgramWizardStepValue, { component: ReactNode; actionProps: Partial<ModalProps> }> = {
    [ProgramWizardStep.SELECT_PROGRAM]: {
      component: (
        <StepSelectProgram
          currentProgram={currentProgram}
          selected={selectedProgram}
          onSelect={setSelectedProgram}
          isMobile={isMobile}
        />
      ),
      actionProps: {
        primaryButtonTitle: t('next'),
        primaryButtonProps: { disabled: !selectedProgram },
        onCancel: handleClose,
        onPrimaryButtonClick: () => {
          if (!selectedProgram) {
            return;
          }
          reset();
          setWizardData((data) => ({
            ...getInitialWizardData(data.batteryId),
            operatingMode: selectedProgram.operatingMode ?? null,
            shutDown24vEnabled: getShutDown24vEnabled(selectedProgram.operatingMode ?? null),
          }));
          setStep(selectedProgram.nextStep);
        },
      },
    },
    [ProgramWizardStep.PEAKSHAVE_MODE]: {
      component: (
        <StepPeakshaveMode
          currentProgram={currentProgram}
          selected={selectedPeakshaveMode}
          onSelect={setSelectedPeakshaveMode}
        />
      ),
      actionProps: {
        primaryButtonTitle: t('next'),
        primaryButtonProps: { disabled: !selectedPeakshaveMode },
        secondaryButtonTitle: t('back'),
        onSecondaryButtonClick: () => setStep(ProgramWizardStep.SELECT_PROGRAM),
        onCancel: handleClose,
        onPrimaryButtonClick: () => {
          if (!selectedPeakshaveMode) {
            return;
          }
          reset();
          setWizardData((data) => ({
            ...getInitialWizardData(data.batteryId),
            operatingMode: selectedPeakshaveMode.operatingMode,
            shutDown24vEnabled: getShutDown24vEnabled(selectedPeakshaveMode.operatingMode),
          }));
          setStep(ProgramWizardStep.INPUT_SETTINGS);
        },
      },
    },
    [ProgramWizardStep.INPUT_SETTINGS]: {
      component: (
        <StepInputSettings
          wizardData={wizardData}
          setWizardData={setWizardData}
          NumberFieldComponent={NumberFieldComponent}
          control={control}
        />
      ),
      actionProps: {
        primaryButtonTitle: t('next'),
        secondaryButtonTitle: t('back'),
        onSecondaryButtonClick: () => setStep(ProgramWizardStep.SELECT_PROGRAM),
        onCancel: handleClose,
        onPrimaryButtonClick: () =>
          handleSubmit((data) => {
            setWizardData((wData) =>
              applyInputSettingsData({
                wizardData: wData,
                data: data as { maxInputCurrent: string; genSetData1: string },
              })
            );
            setStep(ProgramWizardStep.CONFIRM);
          })(),
      },
    },
    [ProgramWizardStep.CONFIRM]: {
      component: <StepConfirm wizardData={wizardData} getBatteryConfig={getBatteryConfig} />,
      actionProps: {
        primaryButtonTitle:
          wizardData.operatingMode === ProgramWizardOperatingMode.SHUTDOWN ? t('proceed') : t('confirm'),
        secondaryButtonTitle: t('back'),
        primaryButtonProps: {
          disabled: isSubmitting,
          startIcon: isSubmitting ? <CircularProgress color="inherit" size={20} /> : undefined,
        },
        onSecondaryButtonClick: () => {
          setShowShutdownInputAlert(false);
          setStep(getStepBeforeConfirm(wizardData.operatingMode));
        },
        onCancel: handleClose,
        onPrimaryButtonClick: () => {
          if (
            wizardData.operatingMode === ProgramWizardOperatingMode.SHUTDOWN &&
            isBatteryInputStillConnected(getBatteryInput?.() ?? batteryInput)
          ) {
            setShowShutdownInputAlert(true);
            return;
          }

          setShowShutdownInputAlert(false);
          setIsSubmitting(true);
          submitBatteryProgram({
            wizardData,
            onSubmit,
            onSuccess: (message) => {
              setIsSubmitting(false);
              onSubmitSuccess?.(message || t('requestSuccess'));
              if (
                wizardData.operatingMode !== null &&
                TRANSITIONING_OPERATING_MODES.includes(wizardData.operatingMode)
              ) {
                // Clear any leftovers of a previous transition before tracking the new one.
                setPhase1CompletedAt(null);
                setShutdownCompletedAt(null);
                setTransitionCompleted(false);
                setActiveTransition({ operatingMode: wizardData.operatingMode, startedAt: Date.now() });
              }
              setStep(ProgramWizardStep.STARTING);
            },
            onError: (error) => {
              setIsSubmitting(false);
              onSubmitError?.(error);
            },
          });
        },
      },
    },
    [ProgramWizardStep.STARTING]: {
      component: (
        <StepProgramStarting
          wizardData={wizardData}
          stateData={stateData}
          isMobile={isMobile}
          transitionWaitComplete={transitionWaitComplete}
          shutdownCompletedAt={shutdownCompletedAt}
          now={now}
          transitionCompleted={transitionCompleted}
        />
      ),
      actionProps: {
        primaryButtonTitle: t('close'),
        onPrimaryButtonClick: handleClose,
      },
    },
  };

  const { component, actionProps } = stepConfigs[step];
  const buttonText = getTriggerButtonText({ label, currentProgram, t });
  const activeTransitionButtonText = activeTransition
    ? activeTransition.operatingMode === ProgramWizardOperatingMode.SHUTDOWN
      ? t('shuttingDown')
      : t('changingProgram')
    : buttonText;
  const dialogTitle = getDialogTitle({ step, currentProgram, t });

  return (
    <>
      <TouchButton
        onClick={openModal}
        variant="contained"
        size={buttonSize}
        fullWidth
        disabled={Boolean(activeTransition)}
        isSpinning={Boolean(activeTransition)}
      >
        {activeTransitionButtonText}
      </TouchButton>

      <Modal
        isOpen={isModalOpen}
        onClose={handleClose}
        title={<span data-testid="dialog-title">{dialogTitle}</span>}
        cancelButtonAlign={cancelButtonAlign ?? (isMobile ? 'left' : undefined)}
        isMobile={isMobile}
        dialogProps={isMobile ? undefined : { PaperProps: { style: paperStyle } }}
        {...actionProps}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {showShutdownInputAlert && (
            <Alert severity="error">
              {t('confirmOperatingModeText.shutdownInputAlert')}
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                <li>{t('confirmOperatingModeText.shutdownInputAlertVoltage')}</li>
                <li>{t('confirmOperatingModeText.shutdownInputAlertBreaker')}</li>
              </Box>
            </Alert>
          )}
          {component}
        </Box>
      </Modal>
    </>
  );
};
